
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import { confirmSwal } from '@/helper/swals';
    import { SweetAlertResult } from 'sweetalert2';
    import Belegposition from '@/types/Belegposition';
    import Beleg from '@/types/Beleg';
    import Planimpfung from '@/types/Planimpfung';
    import Zahlungsart from '@/types/Zahlungsart';
    import BelegPatient from '@/types/BelegPatienten';
    import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';
    import RechnungseingangDialog from '@/components/Rechnungseingang/RechnungseingangDialog.vue';
    import Stornierung from '@/components/Impfverwaltung/Rechnung/Stornierung.vue';

    @Component({
        components: {
            Stornierung,
            RechnungseingangDialog,
        },
        computed: {
            ...mapState('patient', ['loaders', 'rechnungen', 'rechnungenpatienten', 'patientData']),
            ...mapGetters('patient', ['getSelectedRechnung', 'getOffeneImpfungen']),
            ...mapGetters('leistungen', ['getVerwaltungsabgabe']),
            ...mapGetters('authentication', ['getSystemEinstellung']),
            ...mapState('zahlungsarten', ['arten']),
            ...mapState('kassafuehrung', ['offeneZahlscheinBelege', 'dialogs']),
        },
        methods: {
            ...mapActions('patient', [
                'storno',
                'updateBeleg',
                'rechnungpdf',
                'rechnungsxml',
                'rechnungBerichtigung',
                'rechnungMahnung',
            ]),
            ...mapMutations('patient', ['setDruckansicht']),
            ...mapActions('zahlungsarten', ['loadZahlungsarten']),
            ...mapMutations('kassafuehrung', ['setDialogZahlungseingang', 'setBeleg', 'setDialogStornieren']),
        },
    })
    export default class Druckansicht extends Vue {
        @Prop() visible!: boolean;

        public getOffeneImpfungen!: Planimpfung[];
        public arten!: Zahlungsart[];
        public rechnungenpatienten!: BelegPatient[];
        public loadZahlungsarten!: () => void;
        public updateBeleg!: (beleg: Beleg) => Promise<boolean>;
        public getSelectedRechnung!: { rechnung: Beleg, positionen: Belegposition[] };
        public storno!: (payload: { anmerkung: string }) => void;
        public rechnungBerichtigung!: () => void;
        public rechnungMahnung!: () => void;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;
        public setDialogZahlungseingang!: (payload: { zahlungseingang?: boolean}) => void;
        public setDialogStornieren!: (payload: { stornieren?: boolean, }) => void;
        public setBeleg!: (payload: { beleg: Beleg, load: boolean }) => void;

        public isEdit = false;
        public readonly showGeplantes = false;
        public readonly showBriefHeader = false;

        public beleg = new Beleg();
        public belegpositionen: Belegposition[]= [];

        public sumBruttoBetrag = (betrag: number, pos: Belegposition): number => betrag += pos.betrag_brutto ?? 0;

        public get belegPatienten(): BelegPatient[] {
            return this.rechnungenpatienten.filter(({ beleg_id }) => beleg_id === this.beleg.beleg_id);
        }

        public sumBetragBrutto(sum: number, pos: Belegposition): number {
            return sum += pos.betrag_brutto ?? 0;
        }

        public sumBetragNetto(sum: number, pos: Belegposition): number {
            return sum += pos.betrag ?? 0;
        }

        public calcBruttoNetto(mehrwertsteuer: number): { brutto: number; netto: number; mwst: number } {
            if (mehrwertsteuer === 0) {
                const brutto = this.belegpositionen
                    .filter(({ steuersatz }) => ![10, 20].includes(steuersatz ?? 0))
                    .reduce(this.sumBetragBrutto, 0);

                return {
                    brutto,
                    netto: brutto,
                    mwst: 0,
                };
            }

            const brutto = this.belegpositionen
                .filter(({ steuersatz }) => steuersatz === mehrwertsteuer)
                .reduce(this.sumBetragBrutto, 0);

            const netto = this.belegpositionen
                .filter(({ steuersatz }) => steuersatz === mehrwertsteuer)
                .reduce(this.sumBetragNetto, 0);

            return {
                brutto,
                netto,
                mwst: brutto - netto,
            };
        }

        public get vorraussichtlicherZahlbetrag(): number {
            return this.getOffeneImpfungen.reduce((betrag: number, impfung: Planimpfung) => {
                const verkaufspreis = parseFloat(impfung.impfstoff_verkaufspreis?.toString() ?? '0');
                return betrag += !isNaN(verkaufspreis) ? verkaufspreis : 0;
            }, 0);
        }

        public get rechnungstext(): string {
            const { beleg } = this;

            return this.arten.find(({ zahlungsart }) => zahlungsart === beleg.zahlungsart)?.rechnungstext ??
                beleg.zahlungsart_rechnungstext ?? '';
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                this.onClickCancel();
            }
        }

        @Watch('isEdit')
        public onChangeIsEdit(isEdit: boolean): void {
            if (isEdit) {
                this.loadZahlungsarten();
            }
        }

        public onClickSave(): void {
            this.updateBeleg(this.beleg)
                .then((updated: boolean) => {
                    this.onClickCancel();
                    this.isEdit = !updated;
                });
        }

        public onClickCancel(): void {
            const { rechnung, positionen } = this.getSelectedRechnung;

            this.beleg = new Beleg(rechnung);
            this.belegpositionen = positionen.map((pos: Belegposition) => new Belegposition(pos));

            this.isEdit = false;
        }

        public onClickStornieren(): void {
            this.setDialogStornieren({ stornieren: true });
            /*confirmSwal('Stornieren', 'Wollen Sie die Rechnung wirklich stornieren?', 'Ja', 'Nein')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.storno();
                    }
                });*/
        }

        public onClickBerichtigt(): void {
            confirmSwal('Berichtigung', 'Wollen Sie die Rechnung als berichtigt kennzeichnen?', 'Ja', 'Nein')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.rechnungBerichtigung();
                    }
                });
        }

        public onClickGemahnt(): void {
            confirmSwal('Mahnung', 'Wollen Sie die Rechnung als gemahnt kennzeichnen?', 'Ja', 'Nein')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.rechnungMahnung();
                    }
                });
        }

        public openRechnung(item: Beleg) {
            this.setDialogZahlungseingang({ zahlungseingang: true });
            this.setBeleg({beleg: item, load: true});
        }
    }
